/**
 * Tab Focus Guard — container.
 *
 * Wraps a `(tabs)` screen's content with `web:pointer-events-none`
 * while the tab is blurred. expo-router's `Tabs` keeps non-focused
 * tab screens mounted on web (it relies on visual stacking +
 * visibility for the "active" feel). On native, `react-native-screens`
 * already detaches inactive tabs, so the `web:` scoping leaves native
 * untouched.
 *
 * Why this exists: several screens render fixed-height (`h-96`)
 * `HStack` rows or kanban grids that sit in DOM positions which
 * overlap the focused tab's content. Browser hit-testing follows
 * visual stacking — a real user clicking on the focused tab always
 * lands on the focused tab — but Playwright's stricter DOM hit-test
 * classifies the inactive sibling as the click interceptor and the
 * click is rerouted into the blurred screen, producing nondeterministic
 * shard failures (most recently seen on `big-boards.spec.ts`,
 * `scouting-reports.spec.ts`, `shadow-team.spec.ts`, and the
 * `contracts/*` subtab specs after PR #3592 cleared the dashboard
 * half of the same bug).
 *
 * Per `.claude/rules/product-fix-for-test-pass.md`: this does NOT
 * change visible user behavior — the inactive tab cannot be
 * interacted with while the user is on a different tab regardless.
 * The change just makes Playwright's stricter DOM hit-test match
 * real-browser behavior.
 *
 * Usage:
 *
 * ```tsx
 * // app/(root)/(tabs)/scouting.tsx
 * const ScoutingRoute = () => (
 *   <TabFocusGuard>
 *     <Main />
 *   </TabFocusGuard>
 * );
 * ```
 *
 * The Home tab wraps its route entry in this component too — see
 * `app/(root)/(tabs)/index.tsx`. (It previously applied the pattern
 * inline; that guard was dropped when Home moved to `features/home`
 * in the design-system tiers migration, so the route now uses this
 * shared component like every other tab.)
 * @module components/TabFocusGuard/TabFocusGuardContainer
 */

import { useIsFocused } from "expo-router/react-navigation";
import React from "react";

import TabFocusGuardView from "./TabFocusGuardView";

/** Props for {@link TabFocusGuardContainer}. */
interface Props {
  /** The tab screen content to render. */
  readonly children: React.ReactNode;
}

/**
 * Container that reads the tab's focus state via
 * {@link useIsFocused} and forwards it to {@link TabFocusGuardView}.
 * @param props - Container props
 * @param props.children - The tab screen content to render
 */
function TabFocusGuardContainer({ children }: Props) {
  const isFocused = useIsFocused();
  return (
    <TabFocusGuardView isFocused={isFocused}>{children}</TabFocusGuardView>
  );
}

TabFocusGuardContainer.displayName = "TabFocusGuardContainer";

export default TabFocusGuardContainer;
